<template>
  <settings-section
    :title="$t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.TITLE')"
    :sub-title="$t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.SUBTITLE')"
  >
    <!-- El SDK de Meta se descarga solo cuando el usuario se acerca al botón: esta sección se
         muestra en cada visita a la configuración de la bandeja y casi nunca se reconecta. -->
    <div
      class="whatsapp-reauthorize"
      @mouseenter.once="preloadSignupSdk"
      @focusin.once="preloadSignupSdk"
    >
      <!-- El aviso del número va siempre visible, en color de alerta y con título (el icono solo
           se pinta con título), igual que en el alta: el backend rechaza cualquier otro número.
           El título de "expiró" solo cuando la conexión cayó. -->
      <warning-callout
        :title="
          inbox.reauthorization_required
            ? $t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.EXPIRED')
            : $t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.SAME_NUMBER_TITLE')
        "
        type="warning"
      >
        <!-- El número va en negritas dentro del texto traducido; se sanea como en el alta. -->
        <p
          v-dompurify-html="
            $t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.SAME_NUMBER_HINT', {
              phoneNumber: inbox.phone_number,
            })
          "
        />
      </warning-callout>
      <woot-submit-button
        :loading="isProcessing || isCheckingMode"
        icon-class="brand-facebook"
        :button-text="
          isProcessing
            ? $t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.IN_PROGRESS')
            : $t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.BUTTON')
        "
        type="button"
        @click="reconnect"
      />
    </div>
  </settings-section>
</template>

<script>
import SettingsSection from '../../../../../components/SettingsSection';
import WarningCallout from 'dashboard/components/ui/WarningCallout';
import alertMixin from 'shared/mixins/alertMixin';
import embeddedSignupMixin from '../channels/whatsapp/embeddedSignupMixin';
import WhatsappAuthorization from 'dashboard/api/whatsappAuthorization';
import {
  UNSUPPORTED_FLOW,
  storedOnBizApp,
} from '../channels/whatsapp/embeddedSignupUtils';

// Reautorización de una bandeja WhatsApp Cloud con el popup de Meta, homóloga a la de Facebook:
// renueva el token y la suscripción de webhooks sin pegar credenciales a mano. Aplica también a
// bandejas dadas de alta a mano, que quedan en la app de Intersel. El backend exige que el número
// elegido sea el mismo.
export default {
  components: {
    SettingsSection,
    WarningCallout,
  },
  mixins: [alertMixin, embeddedSignupMixin],
  // Si el usuario sale de la pantalla con el popup abierto, Meta termina igual su parte: la
  // reconexión se completa y el aviso sale por el toast global.
  keepSignupOnDestroy: true,
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isProcessing: false,
      // true coexistencia, false solo Cloud API, null sin saber (se abre en modo normal).
      onBizApp: null,
      isCheckingMode: false,
    };
  },
  watch: {
    // Settings reutiliza el componente al cambiar de bandeja: el modo se vuelve a resolver.
    'inbox.id'() {
      this.resolveMode();
    },
  },
  created() {
    // Con la línea caída lo más probable es que el usuario venga a reconectarla: SDK listo desde ya.
    if (this.inbox.reauthorization_required) this.preloadSignupSdk();
    this.resolveMode();
  },
  methods: {
    // Toma el modo guardado de la bandeja; si no lo tiene, se lo pide al backend antes del clic,
    // porque el popup solo abre dentro de la activación del usuario y no puede esperar una petición.
    // Cualquier fallo deja el modo sin saber y el botón disponible.
    async resolveMode() {
      const inboxId = this.inbox.id;
      this.onBizApp = storedOnBizApp(this.inbox);
      if (this.onBizApp !== null) return;

      this.isCheckingMode = true;
      try {
        const { data } = await WhatsappAuthorization.getBizAppStatus(inboxId);
        // Se ignora una respuesta tardía de otra bandeja.
        if (inboxId === this.inbox.id && typeof data.on_biz_app === 'boolean') {
          this.onBizApp = data.on_biz_app;
        }
      } catch (error) {
        // Sin el dato se abre el popup en modo normal.
      } finally {
        if (inboxId === this.inbox.id) this.isCheckingMode = false;
      }
    },
    // Abre el popup de Meta en el modo de la línea (coexistencia o solo Cloud API) y manda
    // code + inbox_id al backend para renovar las credenciales.
    async reconnect() {
      if (this.isProcessing || this.isCheckingMode) return;
      this.isProcessing = true;
      // Se fija al hacer clic: Settings reutiliza este componente al cambiar de bandeja y el
      // popup puede terminar cuando el prop ya apunta a otra.
      const inboxId = this.inbox.id;

      try {
        const signupData = await this.runSignup({
          coexistence: this.onBizApp === true,
        });
        // null = el usuario cerró/canceló el popup; mismo aviso neutro que en el alta.
        if (!signupData) {
          this.showAlert(this.$t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.CANCELLED'));
          return;
        }

        const updatedInbox = await this.$store.dispatch(
          'inboxes/reauthorizeWhatsAppEmbeddedSignup',
          {
            ...signupData,
            inbox_id: inboxId,
          }
        );
        // La reconexión deja guardado el modo vigente del número (si se sigue en esa bandeja).
        const refreshed = storedOnBizApp(updatedInbox);
        if (refreshed !== null && inboxId === this.inbox.id) {
          this.onBizApp = refreshed;
        }
        this.showAlertSuccess(
          this.$t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.MESSAGE_SUCCESS')
        );
      } catch (error) {
        // Corrida cancelada a propósito: no es un fallo que avisar.
        if (this.isSignupAborted(error)) return;
        this.showAlertError(this.errorMessage(error));
      } finally {
        this.isProcessing = false;
      }
    },
    // Mensaje según el origen del fallo: flujo sin número que conectar, error del backend, error
    // que Meta mostró en el popup, o genérico.
    errorMessage(error) {
      if (error && error.message === UNSUPPORTED_FLOW) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.UNSUPPORTED');
      }
      return (
        this.signupApiError(error) ||
        this.signupPopupError(error) ||
        this.$t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.MESSAGE_ERROR')
      );
    },
  },
};
</script>
